// Phase SA-5 — Structured leg builder (brief §4.2).
//
// Each leg is a small form: fixture / market / selection / odds /
// outcome / remove. The builder lists rows + adds new ones + shows
// live combined odds. Replaces the raw text box as the default
// input method, producing structured data the agent doesn't have
// to parse.

import {
  BUILDER_MARKETS,
  combinedOdds,
  emptyLeg,
  formatCombined,
  type StructuredLeg,
  type LegOutcome,
} from "./legHelpers.ts";

export interface StructuredLegBuilderProps {
  legs: ReadonlyArray<StructuredLeg>;
  onChange: (next: StructuredLeg[]) => void;
}

const LEG_OUTCOMES: ReadonlyArray<{ value: LegOutcome; label: string }> = [
  { value: "won", label: "W" },
  { value: "lost", label: "L" },
  { value: "void", label: "V" },
  { value: "unknown", label: "?" },
];

export function StructuredLegBuilder({
  legs,
  onChange,
}: StructuredLegBuilderProps): JSX.Element {
  const totalOdds = combinedOdds(legs);
  const update = (index: number, patch: Partial<StructuredLeg>): void => {
    const next = legs.map((l, i) => (i === index ? { ...l, ...patch } : l));
    onChange(next);
  };
  const remove = (index: number): void => {
    onChange(legs.filter((_, i) => i !== index));
  };
  const add = (): void => {
    onChange([...legs, emptyLeg()]);
  };

  return (
    <div data-testid="sa-structured-builder">
      <div className="sa-leg-rows">
        {legs.length === 0 ? (
          <div
            data-testid="sa-builder-empty"
            style={{
              padding: "20px 16px",
              border: "1px dashed var(--sa-hairline)",
              borderRadius: 4,
              background: "var(--sa-bg-raised)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              color: "var(--sa-fg-muted)",
              textAlign: "center",
            }}
          >
            No legs yet. Click +&nbsp;Add another leg below to start.
          </div>
        ) : (
          legs.map((leg, i) => (
            <LegRow
              key={leg.local_id}
              index={i}
              leg={leg}
              onUpdate={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))
        )}
      </div>

      <button
        type="button"
        className="sa-add-leg-btn"
        onClick={add}
        data-testid="sa-builder-add"
      >
        + Add another leg
      </button>

      <div
        className="sa-combined-odds"
        data-testid="sa-combined-odds"
        aria-live="polite"
      >
        Combined odds:{" "}
        <span className="sa-combined-odds-value" data-testid="sa-combined-odds-value">
          {formatCombined(legs)}
        </span>
        {totalOdds != null && legs.length > 1 ? (
          <span
            style={{
              marginLeft: 10,
              color: "var(--sa-fg-muted)",
              fontSize: 11,
            }}
          >
            ({legs.filter((l) => l.outcome !== "void" && l.odds != null).map((l) => (l.odds as number).toFixed(2)).join(" × ")})
          </span>
        ) : null}
      </div>
    </div>
  );
}

function LegRow({
  index,
  leg,
  onUpdate,
  onRemove,
}: {
  index: number;
  leg: StructuredLeg;
  onUpdate: (patch: Partial<StructuredLeg>) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div
      className="sa-leg-row"
      data-testid={`sa-leg-row-${index}`}
      role="group"
      aria-label={`Leg ${index + 1}`}
    >
      <input
        type="text"
        className="sa-form-input"
        placeholder="Brighton vs Bournemouth"
        value={leg.fixture}
        onChange={(e) => onUpdate({ fixture: e.target.value })}
        aria-label="Fixture"
        data-testid={`sa-leg-${index}-fixture`}
      />
      <select
        className="sa-form-select"
        value={leg.market}
        onChange={(e) => onUpdate({ market: e.target.value })}
        aria-label="Market"
        data-testid={`sa-leg-${index}-market`}
      >
        {BUILDER_MARKETS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <input
        type="text"
        className="sa-form-input"
        placeholder="Home / Yes / Over"
        value={leg.selection}
        onChange={(e) => onUpdate({ selection: e.target.value })}
        aria-label="Selection"
        data-testid={`sa-leg-${index}-selection`}
      />
      <input
        type="number"
        step="0.01"
        min="1"
        className="sa-form-input"
        placeholder="1.72"
        value={leg.odds ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          onUpdate({ odds: v === "" ? null : Number(v) });
        }}
        aria-label="Odds"
        data-testid={`sa-leg-${index}-odds`}
      />
      <div
        className="sa-outcome-buttons"
        role="radiogroup"
        aria-label={`Leg ${index + 1} outcome`}
        data-testid={`sa-leg-${index}-outcome`}
      >
        {LEG_OUTCOMES.map((o) => {
          const selected = o.value === leg.outcome;
          return (
            <button
              key={o.value}
              type="button"
              className="sa-outcome-btn"
              data-outcome={o.value}
              data-selected={selected || undefined}
              data-testid={`sa-leg-${index}-outcome-${o.value}`}
              role="radio"
              aria-checked={selected}
              onClick={() => onUpdate({ outcome: o.value })}
              title={
                o.value === "won"
                  ? "Won"
                  : o.value === "lost"
                    ? "Lost"
                    : o.value === "void"
                      ? "Void"
                      : "Unknown"
              }
              style={{ padding: "4px 8px", minWidth: 28 }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="sa-leg-remove"
        onClick={onRemove}
        data-testid={`sa-leg-${index}-remove`}
        aria-label={`Remove leg ${index + 1}`}
        title="Remove leg"
      >
        ×
      </button>
    </div>
  );
}
